/* Progressive enhancement: native variants/forms remain the source of truth. */
(() => {
  const mount = (root) => {
    if (root.dataset.pdpMounted) return;
    const button = root.querySelector('.product-information__buy-buttons button[name="add"]');
    const selector = root.querySelector('variant-selects:not([data-sticky="1"])');
    const size = selector && [...selector.querySelectorAll('fieldset')].find((field) =>
      /talla|size/.test(field.dataset.handle || '') && !field.hidden);
    if (!button || !size) return;
    root.dataset.pdpMounted = 'true';
    const form = button.form;
    const label = button.querySelector('.single-add-to-cart-button--text');
    const nativeLabel = label.textContent.trim();
    const sticky = root.querySelector('[data-pdp-sticky]');
    const stickyButton = root.querySelector('[data-pdp-sticky-button]');
    const status = root.querySelector('[data-pdp-sticky-status]');
    const abort = new AbortController();
    let chosen = root.dataset.explicitVariant === 'true';
    let pending = false;
    const caption = size.querySelector('.form__label-text');
    const originalCaption = caption ? caption.textContent.trim() : '';
    let sizeUnit = 'mx';
    size.setAttribute('tabindex', '-1');
    size.dataset.pdpSize = '';
    const help = document.createElement('p');
    help.className = 'vv-pdp-size-help';
    help.id = 'PdpSizeHelp-' + root.dataset.section;
    help.setAttribute('aria-live', 'polite');
    size.after(help);
    button.setAttribute('aria-describedby', help.id);

    const selected = () => size.querySelector('input:checked, select');
    const selectedSize = () => {
      const control = selected();
      if (!control) return { mx: '', us: '' };
      const source = control.tagName === 'SELECT' ? control.options[control.selectedIndex] : control;
      return {
        mx: source?.dataset?.sizeMx || control.value || '',
        us: source?.dataset?.sizeUs || '',
      };
    };
    const applySizeUnit = (unit) => {
      sizeUnit = unit === 'us' ? 'us' : 'mx';
      root.dataset.sizeUnit = sizeUnit;
      root.querySelectorAll('[data-vv-size-label], [data-vv-size-picker] option[data-size-mx]').forEach((label) => {
        const converted = label.dataset[sizeUnit === 'us' ? 'sizeUs' : 'sizeMx'];
        label.textContent = converted || (sizeUnit === 'us' ? 'MX ' + label.dataset.sizeMx : label.dataset.sizeMx);
      });
      root.querySelectorAll('[data-vv-size-toggle] button').forEach((unitButton) => {
        unitButton.setAttribute('aria-pressed', String(unitButton.dataset.sizeUnit === sizeUnit));
      });
    };
    const opensSoldOutModal = () => button.dataset.soldOutTrigger === 'true';
    const nativeBusy = () => button.disabled || button.classList.contains('loading') ||
      (button.getAttribute('aria-disabled') === 'true' && !opensSoldOutModal());
    const purchaseBlocked = () => pending || nativeBusy();
    const refresh = () => {
      root.classList.toggle('vv-pdp--needs-size', !chosen);
      const value = selected()?.value || '';
      const displaySize = selectedSize();
      const sold = opensSoldOutModal();
      const nextLabel = !chosen ? 'Selecciona tu talla' : label.textContent.trim();
      if (!chosen && label.textContent.trim() !== nextLabel) label.textContent = nextLabel;
      const nextHelp = chosen
        ? sizeUnit === 'us' && displaySize.us
          ? 'Seleccionada: US ' + displaySize.us + ' · equivale a MX ' + displaySize.mx
          : 'Seleccionada: MX ' + displaySize.mx + (displaySize.us ? ' · equivale a US ' + displaySize.us : '')
        : 'Elige tu talla antes de agregar este producto.';
      if (help.textContent !== nextHelp) help.textContent = nextHelp;
      if (caption) caption.textContent = chosen ? originalCaption : 'Selecciona tu talla';
      const nextSticky = !chosen ? 'Elegir talla' : sold ? 'Consultar talla' : label.textContent.trim();
      if (stickyButton.textContent !== nextSticky) stickyButton.textContent = nextSticky;
      status.textContent = chosen
        ? 'Talla ' + (sizeUnit === 'us' && displaySize.us ? 'US ' + displaySize.us : 'MX ' + value)
        : 'Elige tu talla';
      stickyButton.disabled = chosen && purchaseBlocked();
    };
    const focusSize = () => {
      size.scrollIntoView({ block: 'center', behavior: 'auto' });
      size.focus({ preventScroll: true });
    };
    const confirmSize = () => {
      if (!selected()) return;
      chosen = true;
      // The native variant handler owns sold-out/preorder/loading state.
      if (label.textContent.trim() === 'Selecciona tu talla') label.textContent = nativeLabel;
      refresh();
    };
    size.addEventListener('click', (event) => {
      if (event.target.closest('label, input, option')) confirmSize();
      if (event.target.matches('input[type="radio"]:checked') &&
          selector.dataset.variantLoadError === 'true') selector.onVariantChange();
    }, { signal: abort.signal });
    size.addEventListener('change', confirmSize, { signal: abort.signal });
    root.querySelectorAll('[data-vv-size-toggle] button').forEach((unitButton) => {
      unitButton.addEventListener('click', () => {
        applySizeUnit(unitButton.dataset.sizeUnit);
        refresh();
      }, { signal: abort.signal });
    });
    // A same-value radio click is still an explicit choice. Capturing the submit
    // prevents keyboard submission from bypassing selection.
    root.addEventListener('click', (event) => {
      if ((!chosen || purchaseBlocked()) && event.target.closest('.product-information__buy-buttons button[name="add"]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!chosen) focusSize();
      }
    }, { capture: true, signal: abort.signal });
    form.addEventListener('submit', (event) => {
      if (!chosen || purchaseBlocked() || opensSoldOutModal()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!chosen) focusSize();
      }
    }, { capture: true, signal: abort.signal });
    selector.addEventListener('change', () => {
      pending = true;
      refresh();
    }, { signal: abort.signal });
    const mutation = new MutationObserver(() => {
      if (!nativeBusy()) pending = false;
      refresh();
    });
    mutation.observe(button, { attributes: true, childList: true, subtree: true });
    stickyButton.addEventListener('click', () => {
      if (!chosen) return focusSize();
      button.scrollIntoView({ block: 'center', behavior: 'auto' });
      button.focus({ preventScroll: true });
      if (!purchaseBlocked()) button.click();
    }, { signal: abort.signal });
    const protectPurchaseControls = () => {
      const stickyHeight = sticky.hidden ? 0 : sticky.getBoundingClientRect().height;
      const chatHeight = document.querySelector('#chat-bubble')?.getBoundingClientRect().height || 56;
      let clearance = stickyHeight > 0 ? stickyHeight + 14 : 10;
      // Reserve space for both native purchase and care add-on buttons when they
      // cross the floating widget's band. Work bottom-up if several are visible.
      const controls = [...root.querySelectorAll('.product-information__buy-buttons button[name="add"], .boot-ritual button')]
        .map(control => control.getBoundingClientRect()).filter(rect => rect.width > 0)
        .sort((a, b) => b.top - a.top);
      controls.forEach(rect => {
        const chatBottom = window.innerHeight - clearance;
        if (rect.top < chatBottom && rect.bottom > chatBottom - chatHeight) {
          clearance = Math.max(clearance, window.innerHeight - rect.top + 14);
        }
      });
      document.documentElement.style.setProperty('--vv-pdp-chat-clearance', clearance + 'px');
    };
    document.addEventListener('scroll', protectPurchaseControls, { passive: true, signal: abort.signal });
    window.addEventListener('resize', protectPurchaseControls, { passive: true, signal: abort.signal });
    const intersection = new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting;
      protectPurchaseControls();
    }, { threshold: 0.1 });
    intersection.observe(button);
    // The external WhatsApp widget must clear the actual bar height, including
    // wrapped text and the safe area. ResizeObserver also catches font changes.
    const stickyResize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => {
      document.documentElement.style.setProperty('--vv-pdp-sticky-height', sticky.getBoundingClientRect().height + 'px');
      protectPurchaseControls();
    });
    stickyResize?.observe(sticky);
    protectPurchaseControls();
    root.querySelectorAll('.product-360-video video').forEach((video) => {
      const details = video.closest('details');
      if (!details) return;
      video.removeAttribute('autoplay');
      video.controls = true;
      const toggle = () => {
        if (!details.open) video.pause();
      };
      details.addEventListener('toggle', toggle, { signal: abort.signal });
      toggle();
    });
    applySizeUnit(sizeUnit);
    refresh();
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail.sectionId !== root.dataset.section) return;
      abort.abort();
      mutation.disconnect();
      intersection.disconnect();
      stickyResize?.disconnect();
      document.documentElement?.style.removeProperty('--vv-pdp-sticky-height');
      document.documentElement?.style.removeProperty('--vv-pdp-chat-clearance');
    }, { signal: abort.signal });
  };
  const init = () => document.querySelectorAll('[data-vv-pdp]').forEach(mount);
  init();
  document.addEventListener('shopify:section:load', init);
})();
